<script setup lang="ts">
//#region imports
import { cloneDeep } from 'lodash-es';
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { isErrorResponse, type EmitsToListeners, type FormErrors } from '@/types/core';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type {
  GetAdminsByIdAffiliatesData,
  GetAdminsByIdAffiliatesResponse,
  GetAdminsByIdResponse,
} from '@/types/openapi-ts';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
//#endregion

//#region types
type UserInfo = GetAdminsByIdResponse['payload'];
type PopupUserInfo = Pick<UserInfo, 'id' | 'name'>;
type Affiliate = GetAdminsByIdAffiliatesResponse['payload'][number];
type AdminAffiliatesQuery = NonNullable<GetAdminsByIdAffiliatesData['query']>;
type GetAdminAffiliatesParams = AdminAffiliatesQuery & {
  type?: 'unattached';
};

interface Props {
  teamId: string;
  userInfo: PopupUserInfo;
}

interface Emits {
  refresh: [];
  close: [];
}

export interface PinAffiliateUserParams {
  data: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
//#endregion

//#region const
const LS_PATH = 'PinAffiliatesUser';

const sizes = {
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<Affiliate>[] = [
  {
    name: 'full_name',
    sortable: 'custom',
  },
  {
    name: 'email',
    sortable: 'custom',
  },
];
//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
const { searchQuery } = useTableSearch();
const { limit, count, page } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions: {
    sort_column: 'email',
    sort_dir: 'asc',
  },
  lsPath: LS_PATH,
  fields,
});
//#endregion

//#region state
const isVisible = ref(false);
const isDataLoading = ref(false);
const isSaving = ref(false);
const affiliateId = ref<string | null>(null);
const affiliates = ref<Affiliate[]>([]);
const errors = ref<FormErrors>({});
//#endregion

//#region computed
const query = computed(() => ({
  search: searchQuery.value,
  type: 'unattached' as const,
  ...optionsSort.value,
  limit: limit.value,
  offset: (limit.value * page.value) - limit.value,
} as GetAdminAffiliatesParams));
//#endregion

//#region methods
const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') {
    return;
  }

  sortProp.value = prop;
  sortOrder.value = order;
  page.value = 1;

  nextTick(() => {
    getAffiliates();
  });
};

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    getAffiliates();
  }
};

function setErrors(error: unknown) {
  if (!isErrorResponse(error)) {
    throw error;
  }

  errors.value = cloneDeep(error.data.errors) ?? {};
}

function getAffiliates() {
  if (!props.userInfo.id) {
    return;
  }

  isDataLoading.value = true;
  errors.value = {};

  api.getAdminAffiliates(props.userInfo.id, query.value).then((response) => {
    count.value = response.data.misc?.count ?? 0;
    affiliates.value = response.data.payload;
  }).catch(setErrors).finally(() => {
    isDataLoading.value = false;
  });
}

function checkedRows(_id: string, ids: string[]) {
  affiliateId.value = ids[0] ?? null;
}

function open() {
  isVisible.value = true;
  getAffiliates();
}

function handleClose() {
  isVisible.value = false;
  searchQuery.value = '';
  affiliateId.value = null;
  affiliates.value = [];
  count.value = 0;
  errors.value = {};
  isDataLoading.value = false;
  isSaving.value = false;
  page.value = 1;
  emit('close');
}

function handleSave() {
  if (!props.userInfo.id || !affiliateId.value) {
    return;
  }

  isSaving.value = true;
  errors.value = {};

  api.postAttachAffiliate(props.userInfo.id, {
    affiliate_id: affiliateId.value,
    team_id: props.teamId,
  }).then(() => {
    emit('refresh');
    handleClose();
  }).catch(setErrors).finally(() => {
    isSaving.value = false;
  });
}
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="pin-affiliate-user"
    :title="t('team_popup.add_affiliates_to_user_title', { name: props.userInfo.name })"
    :width="800"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:flex t:-mx-24 t:-my-16 t:h-full t:flex-col">
        <div class="t:flex t:items-center t:space-x-8 t:px-24 t:py-16">
          <div class="t:w-full">
            <UiInput
              v-model.trim="searchQuery"
              is-search
              autosize
              @search="getAffiliates"
            />
          </div>
          <UiPagination
            :page="page"
            :page-size="limit"
            :count="count"
            @page-change="pageChange"
          />
        </div>
        <UiTableNew
          v-loading="isDataLoading"
          select-row
          is-static
          :fields="fields"
          :data="affiliates"
          :rows-count="limit"
          :padding="0"
          :sort="{
            prop: sortProp,
            order: sortOrder,
          }"
          i18n-path="team_users.list"
          checked-row
          :show-check-all="false"
          checked-row-id="id"
          element-loading-custom-class="data-loader-spinner"
          class="t:overflow-y-hidden"
          select-type="radio"
          @sort="handleSort"
          @checked-rows="checkedRows"
        >
          <template v-slot:append>
            <ElTableColumn :label="t('team_users.list.fields.messenger.title')">
              <template v-slot="scope">
                <UiMessenger
                  class="pin-affiliate-user__messenger"
                  :messenger-type="scope.row.messenger ?? ''"
                  :messenger-address="scope.row.messenger_id ?? ''"
                />
              </template>
            </ElTableColumn>
          </template>
        </UiTableNew>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ t('team_popup.cancel_button') }}
        </UiButton>
        <UiButton
          :disabled="!affiliateId"
          :loading="isSaving"
          filled
          size="big"
          icon="plus"
          @click="handleSave"
        >
          {{ t('team_popup.add_affiliate_button') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.pin-affiliate-user {
  &__messenger .ui-messenger__link {
    font-size: inherit;
  }
}
</style>
